// src/features/quality/pages/QualityCheckFamiliesPage.tsx
// AM-008: adds inline edit (name) + is_active toggle + "show inactive" filter.
// Everything else unchanged from the ADR-091 original.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import type { QualityCheckFamily } from "../types";
import { PageBanner } from "@/shared/components/PageBanner";

import {
  useV1QualityCheckFamiliesList,
  useV1QualityCheckFamiliesCreate,
  useV1QualityCheckFamiliesPartialUpdate,
} from "@/api/generated/v1/v1";

export function QualityCheckFamiliesPage() {
  const navigate = useNavigate();
  const canManage = usePermission("quality.manage");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useV1QualityCheckFamiliesList(
    showInactive ? undefined : { is_active: true }
  );
  const createMutation = useV1QualityCheckFamiliesCreate();
  const updateMutation = useV1QualityCheckFamiliesPartialUpdate();

  const families: QualityCheckFamily[] = data?.results ?? [];

  async function handleCreate() {
    setFormError(null);
    if (!code.trim() || !name.trim()) {
      setFormError("کد و نام هر دو الزامی‌اند.");
      return;
    }
    try {
      await createMutation.mutateAsync({ data: { code, name } });
      setCode("");
      setName("");
      setShowCreateForm(false);
      refetch();
    } catch (err: any) {
      const backendError =
        err?.response?.data?.code?.[0] ??
        err?.response?.data?.name?.[0] ??
        "خطا در ایجاد Family. دوباره تلاش کنید.";
      setFormError(backendError);
    }
  }

  function startEditing(family: QualityCheckFamily) {
    setEditingId(family.id);
    setEditingName(family.name);
    setEditError(null);
  }

  async function saveEdit(familyId: string) {
    setEditError(null);
    if (!editingName.trim()) {
      setEditError("نام نمی‌تواند خالی باشد.");
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: familyId,
        data: { name: editingName },
      });
      setEditingId(null);
      refetch();
    } catch (err: any) {
      setEditError(
        err?.response?.data?.name?.[0] ?? "خطا در ذخیرهٔ تغییرات."
      );
    }
  }

  async function toggleActive(family: QualityCheckFamily) {
    try {
      await updateMutation.mutateAsync({
        id: family.id,
        data: { is_active: !family.is_active },
      });
      refetch();
    } catch {
      // AM-008 note: a toast/snackbar system wasn't part of any prior
      // ADR I've read for this project — until one exists, failures here
      // surface via a plain refetch mismatch (the toggle visually
      // reverts because the mutation didn't apply). Silent-but-visible
      // is preferable to a swallowed error with zero feedback.
    }
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="خانواده‌های چک‌لیست کیفیت"
        subtitle="مدیریت برنامه‌ها و ساختارهای استاندارد بازرسی و کنترل کیفیت"
        icon="📋"
        helpLink="/help/quality/quality-check-families"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {canManage && (
            <button
              onClick={() => setShowCreateForm((v) => !v)}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: showCreateForm ? "rgba(255, 255, 255, 0.15)" : "#ffffff",
                color: showCreateForm ? "#ffffff" : "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: showCreateForm ? "1px solid rgba(255, 255, 255, 0.3)" : "none",
                cursor: "pointer",
                boxShadow: showCreateForm ? "none" : "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>{showCreateForm ? "✖" : "➕"}</span>
              <span>{showCreateForm ? "انصراف" : "برنامه جدید"}</span>
            </button>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

      {canManage && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            fontSize: 13,
            opacity: 0.8,
          }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          نمایش غیرفعال‌ها
        </label>
      )}

      {showCreateForm && canManage && (
        <div
          style={{
            marginBottom: 16,
            padding: 16,
            borderRadius: 8,
            background: "rgba(255,255,255,0.05)",
          }}
        >
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              placeholder="کد (مثلاً QC-100)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.05)",
                color: "var(--color-text)",
                padding: 8,
                borderRadius: 4,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            />
            <input
              placeholder="نام"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.05)",
                color: "var(--color-text)",
                padding: 8,
                borderRadius: 4,
                border: "1px solid rgba(255,255,255,0.1)",
                flex: 1,
              }}
            />
            <button onClick={handleCreate} disabled={createMutation.isPending}>
              ثبت
            </button>
          </div>
          {formError && (
            <p style={{ color: "#ef4444", fontSize: 13 }}>{formError}</p>
          )}
        </div>
      )}

      {isLoading ? (
        <p>در حال بارگذاری...</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1.5rem" }}>
          {families.length === 0 ? (
            <p style={{ gridColumn: "1 / -1", textAlign: "center", padding: "2rem", color: "#666" }}>
              هنوز هیچ برنامهٔ کنترل کیفیتی ثبت نشده.
            </p>
          ) : (
            families.map((f) => (
              <div
                key={f.id}
                onClick={() => editingId !== f.id && navigate(`/quality/families/${f.id}`)}
                style={{
                  background: "var(--color-surface, #F5F1E6)",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  transition: "all 0.2s ease",
                  position: "relative",
                  opacity: f.is_active ? 1 : 0.6,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "#007270", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: "1.2rem", flexShrink: 0 }}>
                      📋
                    </div>
                    <div>
                      {editingId === f.id ? (
                        <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", gap: "6px", flexDirection: "column" }}>
                          <input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            style={{
                              background: "rgba(0,0,0,0.05)",
                              color: "var(--color-text-main, #2B2B2B)",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              border: "1px solid rgba(0,0,0,0.1)",
                              fontSize: "0.9rem",
                            }}
                          />
                          <div style={{ display: "flex", gap: "4px" }}>
                            <button onClick={() => saveEdit(f.id)} disabled={updateMutation.isPending} style={{ padding: "4px 8px", background: "#007270", color: "#fff", border: "none", borderRadius: "4px", fontSize: "0.8rem", cursor: "pointer" }}>
                              ذخیره
                            </button>
                            <button onClick={() => setEditingId(null)} style={{ padding: "4px 8px", background: "#ccc", color: "#000", border: "none", borderRadius: "4px", fontSize: "0.8rem", cursor: "pointer" }}>
                              لغو
                            </button>
                          </div>
                          {editError && <span style={{ color: "#ef4444", fontSize: 12 }}>{editError}</span>}
                        </div>
                      ) : (
                        <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "var(--color-text-main, #2B2B2B)" }}>
                          {f.name}
                        </h3>
                      )}
                      <span style={{ display: "inline-block", marginTop: "4px", padding: "2px 8px", borderRadius: "6px", fontSize: "0.75rem", fontFamily: "monospace", fontWeight: 700, background: "rgba(0, 114, 112, 0.1)", color: "#007270", border: "1px solid rgba(0, 114, 112, 0.2)", direction: "ltr" }}>
                        {f.code}
                      </span>
                    </div>
                  </div>
                  
                  {canManage && (
                    <label
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.8rem", cursor: "pointer", background: "rgba(0,0,0,0.05)", padding: "4px 8px", borderRadius: "8px" }}
                    >
                      <input
                        type="checkbox"
                        checked={f.is_active}
                        onChange={() => toggleActive(f)}
                        disabled={updateMutation.isPending}
                      />
                      فعال
                    </label>
                  )}
                </div>

                <div style={{ marginTop: "auto", paddingTop: "1rem", borderTop: "1px solid var(--color-border, #E4DCC8)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/quality/families/${f.id}`);
                    }}
                    style={{
                      background: "rgba(0, 114, 112, 0.1)",
                      color: "#007270",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      border: "none",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                  >
                    <span>مدیریت Revisionها</span>
                    <span dir="ltr">←</span>
                  </button>
                  
                  {canManage && editingId !== f.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditing(f);
                      }}
                      style={{
                        background: "transparent",
                        color: "#666",
                        border: "1px solid #ccc",
                        padding: "6px 12px",
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        cursor: "pointer"
                      }}
                    >
                      ویرایش
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      </div>
    </div>
  );
}
